import { z } from 'zod';
import { AutonomyClass } from './actions.ts';
import { SemVer } from './common.ts';
import { AgentOutcomeSpec } from './resolution.ts';

/** Hard limits enforced by the runtime wrapper, never by the prompt. */
export const AgentLimits = z
  .object({
    maxModelTurns: z.number().int().positive(),
    maxToolCalls: z.number().int().nonnegative(),
    maxDelegationDepth: z.number().int().nonnegative().default(0),
    maxConcurrentChildren: z.number().int().nonnegative().default(0),
    deadlineSeconds: z.number().int().positive(),
  })
  .meta({ id: 'AgentLimits' });
export type AgentLimits = z.infer<typeof AgentLimits>;

/** A capability an agent serves; task `capability`/`capabilityVersion` resolve to an agent through this. */
export const CapabilityRef = z
  .object({ name: z.string().min(1), version: z.string().min(1).default('1') })
  .meta({ id: 'CapabilityRef' });
export type CapabilityRef = z.infer<typeof CapabilityRef>;

/**
 * Agent manifest (blueprint §12). `spec.autonomy` is the agent's autonomy ceiling;
 * `spec.tools` are tool names as registered in the tool catalog; `spec.prompt.systemRef` is a path
 * relative to the pack root.
 */
export const AgentDefinition = z
  .object({
    apiVersion: z.enum(['agentic.platform/v1', 'enterprise.agents/v1']).default('agentic.platform/v1'),
    kind: z.literal('AgentDefinition'),
    metadata: z.object({
      name: z.string().regex(/^[a-z0-9][a-z0-9.-]*$/, 'agent name must be lowercase kebab/dot case'),
      version: SemVer,
      domain: z.string().min(1),
      businessOwner: z.string().min(1),
      technicalOwner: z.string().min(1),
      description: z.string().optional(),
    }),
    spec: z.object({
      framework: z.string().default('langgraph'),
      workflowType: z.string().min(1),
      capabilities: z.array(CapabilityRef).default([]),
      inputSchemaRef: z.string().min(1),
      outputSchemaRef: z.string().min(1),
      identityProfile: z.string().min(1),
      modelProfile: z.string().min(1),
      policyBundle: z.string().min(1),
      autonomy: AutonomyClass,
      tools: z.array(z.string().min(1)).default([]),
      knowledgeScopes: z.array(z.string().min(1)).default([]),
      memory: z
        .object({
          scope: z.enum(['none', 'task', 'case', 'domain']).default('task'),
          crossDomainWrites: z.boolean().default(false),
        })
        .prefault({}),
      limits: AgentLimits,
      evaluations: z.array(z.string().min(1)).default([]),
      telemetryProfile: z.string().default('default'),
      prompt: z.object({ systemRef: z.string().min(1), version: z.string().min(1) }),
      /**
       * Added by 26a (brief 26 §3.1, §4.2): the agent's business outcome — `resolution` (pinned on a
       * `platform.agent-run` case at start), `gapsFrom` (structured-output paths whose items are gaps) and `gapRoutes`
       * (owner, respondent, channel, deadline, escalation per gap). An A2+ agent must declare `resolution`; A0/A1
       * agents without one get `activity_completed` (§4.6).
       */
      outcome: AgentOutcomeSpec.optional(),
    }),
  })
  .meta({ id: 'AgentDefinition' });
export type AgentDefinition = z.infer<typeof AgentDefinition>;
export type AgentDefinitionInput = z.input<typeof AgentDefinition>;
